import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrialInfo } from '@/hooks/useTrialInfo';
import SharedHeader from '@/components/SharedHeader';
import SharedFooter from '@/components/SharedFooter';
import { 
  Brain, 
  Clock, 
  Shield, 
  BookOpen, 
  MessageCircle, 
  Target,
  Users,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Heart,
  Zap,
  Award,
  BarChart3,
  Trophy,
  Flame,
  Lightbulb,
  ShoppingBag,
  GraduationCap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const WhyEduzy = () => {
  const navigate = useNavigate();
  const { trialDays } = useTrialInfo();

  const crisisStats = [
    { value: '25 Cr', label: 'Students in India', icon: Users, color: 'bg-blue-100 text-blue-600' },
    { value: '65L+', label: 'Dropouts in 5 Years', icon: TrendingDown, color: 'bg-red-100 text-red-600' },
    { value: '8-12%', label: 'Secondary Dropout Rate', icon: AlertTriangle, color: 'bg-amber-100 text-amber-600' },
    { value: '79%', label: 'Class 9-10 Enrolment', icon: BarChart3, color: 'bg-emerald-100 text-emerald-600' }
  ];

  const challenges = [
    { title: 'Learning Gap Explosion', desc: 'Weak foundation meets sudden difficulty in Class 6', icon: TrendingDown, color: 'text-red-500 bg-red-50' },
    { title: 'Curriculum Shock', desc: 'Shift to subject-heavy learning with less attention', icon: BookOpen, color: 'text-orange-500 bg-orange-50' },
    { title: 'Silent Dropouts', desc: 'Students present but not learning', icon: AlertTriangle, color: 'text-amber-500 bg-amber-50' }
  ];

  const benefits = [
    { icon: Clock, title: 'No Travel', desc: 'Learn from home, save 2+ hours daily', color: 'bg-blue-500' },
    { icon: Shield, title: 'Consistent Quality', desc: 'AI adapts to your child\'s level', color: 'bg-emerald-500' },
    { icon: MessageCircle, title: 'Ask Anything', desc: 'Unlimited questions, patient answers', color: 'bg-purple-500' },
    { icon: Target, title: 'Personalized', desc: 'Content matched to their pace', color: 'bg-pink-500' },
    { icon: BarChart3, title: 'Track Progress', desc: 'Daily updates on learning', color: 'bg-cyan-500' },
    { icon: Heart, title: 'No Pressure', desc: 'Learn without judgment', color: 'bg-rose-500' }
  ];

  const gamificationFeatures = [
    { icon: Zap, title: 'XP Points', desc: 'Earn points for every correct answer', color: 'bg-yellow-400' },
    { icon: Trophy, title: 'Leaderboards', desc: 'Compete with peers across India', color: 'bg-amber-500' },
    { icon: Award, title: 'Badges & Levels', desc: 'Unlock achievements as you progress', color: 'bg-purple-500' },
    { icon: Flame, title: 'Daily Streaks', desc: 'Build consistency with streak rewards', color: 'bg-orange-500' }
  ];

  const comparison = [
    { feature: 'Monthly Cost', traditional: '₹3,000-8,000', eduzy: '₹499-899/subject' },
    { feature: 'Travel Time', traditional: '1-2 hours/day', eduzy: 'Zero' },
    { feature: 'Availability', traditional: 'Fixed hours', eduzy: '24/7' },
    { feature: 'Daily Learning', traditional: '1-2 hours fixed', eduzy: 'Up to 4 hours' },
    { feature: 'Practice Tests', traditional: 'Periodic', eduzy: 'Up to Unlimited' },
    { feature: 'Personalization', traditional: 'One-size-fits-all', eduzy: 'AI-adaptive' },
    { feature: 'Questions', traditional: 'Limited', eduzy: 'Unlimited' },
    { feature: 'Progress Tracking', traditional: 'Monthly report card', eduzy: 'Real-time dashboard' },
    { feature: 'Future Skills', traditional: 'None', eduzy: 'AI interaction' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <SharedHeader 
          transparent={true}
          rightContent={
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-600">Home</Button>
              <Button onClick={() => navigate('/login')} className="bg-emerald-500 hover:bg-emerald-600">
                Get Started
              </Button>
            </div>
          }
        />
      </nav>

      {/* Hero Section */}
      <section className="py-10 px-4 bg-gradient-to-br from-emerald-50 via-white to-cyan-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Complete AI-Powered Learning Ecosystem
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 leading-tight">
            Why India&apos;s <span className="text-emerald-600">Working Parents</span> Choose Eduzy
          </h1>
          
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Classes 5-8 are critical. Don&apos;t let learning gaps turn into dropouts. Eduzy offers personalized tutoring, instant homework help, and quality study materials.
          </p>

          {/* CBSE & IB highlight */}
          <div className="flex items-center justify-center mb-6" data-testid="why-eduzy-board-badge">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-emerald-200 rounded-full text-emerald-800 text-sm font-semibold shadow-sm">
              <GraduationCap className="w-4 h-4 text-emerald-600" />
              Built for both <span className="text-emerald-600">CBSE</span> &amp; <span className="text-purple-600">IB</span> curricula — Grades 5-8
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-emerald-500 hover:bg-emerald-600 px-8"
            >
              Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/eduwise')}
              className="px-8"
            >
              View Plans
            </Button>
          </div>
        </div>
      </section>

      {/* AI-Ready Skills Highlight */}
      <section className="py-8 px-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-5">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                <Brain className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <div className="text-center md:text-left flex-1">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                Prepare Your Child for the AI Era
              </h2>
              <p className="text-white/80 text-sm max-w-xl">
                By learning with Eduzy, your child develops <span className="text-white font-semibold">AI interaction skills</span> — 
                the most valuable skill for their future career.
              </p>
            </div>

            <div className="flex-shrink-0">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
                <div className="text-2xl font-bold text-white">85%</div>
                <div className="text-white/70 text-xs">future jobs need AI skills</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Statistics */}
      <section className="py-8 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              The Hidden Crisis in Indian Education
            </h2>
            <p className="text-gray-500 text-sm">Official numbers only tell part of the story</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {crisisStats.map((stat, i) => (
              <div key={i} className={`${stat.color.split(' ')[0]} border border-gray-100 rounded-xl p-4 text-center`}>
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div className="text-xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* The Real Problem */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              The Real Problem: Classes 5-8
            </h3>
            <div className="grid md:grid-cols-3 gap-3">
              {challenges.map((challenge, i) => (
                <div key={i} className={`${challenge.color} rounded-lg p-3`}>
                  <challenge.icon className="w-4 h-4 mb-1" />
                  <h4 className="font-semibold text-gray-900 text-sm">{challenge.title}</h4>
                  <p className="text-gray-600 text-xs">{challenge.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How Eduzy Solves Each Problem */}
      <section className="py-8 px-4 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              How Eduzy Addresses Each Challenge
            </h2>
            <p className="text-gray-500 text-sm">A complete solution for modern education needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* EduWise Solution */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-emerald-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">EduWise</h3>
                  <p className="text-xs text-gray-500">AI Tutor</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Fills Learning Gaps:</strong> AI identifies weak areas and creates personalized lessons</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Daily Practice:</strong> Consistent learning with up to 4 hours/day access</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Parent Visibility:</strong> Real-time dashboard shows what your child is learning</p>
                </div>
              </div>
            </div>

            {/* EduAssist Solution */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-purple-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">EduAssist</h3>
                  <p className="text-xs text-gray-500">Homework Helper</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Instant Help:</strong> Send a photo via WhatsApp, get guidance in seconds</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Teaches, Not Tells:</strong> Socratic method helps them understand</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Always Available:</strong> 24/7 help in 11 Indian languages</p>
                </div>
              </div>
            </div>

            {/* EduQuest Solution */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-amber-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">EduQuest</h3>
                  <p className="text-xs text-gray-500">Learning Shop</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Extra Practice:</strong> Download book-grounded worksheets for any topic</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Exam Prep:</strong> Question banks with varied difficulty levels</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600"><strong>Print & Practice:</strong> Offline-ready PDFs for revision anywhere</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <Badge className="bg-emerald-100 text-emerald-700 border-0 mb-2">
              <Heart className="w-3 h-3 mr-1" />
              For Working Parents
            </Badge>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              Quality Education Without Sacrificing Your Time
            </h2>
            <p className="text-gray-500 text-sm">You work hard for your family. Eduzy ensures your child gets attention.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {benefits.map((benefit, i) => {
              const bgColors = ['bg-blue-50', 'bg-emerald-50', 'bg-purple-50', 'bg-pink-50', 'bg-cyan-50', 'bg-rose-50'];
              return (
                <div key={i} className={`${bgColors[i]} rounded-xl p-4 border border-gray-100`}>
                  <div className={`w-9 h-9 ${benefit.color} rounded-lg flex items-center justify-center mb-2`}>
                    <benefit.icon className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-0.5">{benefit.title}</h3>
                  <p className="text-gray-600 text-xs">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gamification Section */}
      <section className="py-8 px-4 bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <Badge className="bg-purple-100 text-purple-700 border-0 mb-2">
              <Trophy className="w-3 h-3 mr-1" />
              Gamified Learning
            </Badge>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              Learning That Feels Like Playing
            </h2>
            <p className="text-gray-500 text-sm">Compete, earn rewards, and stay motivated</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gamificationFeatures.map((feature, i) => {
              const bgColors = ['bg-yellow-50', 'bg-amber-50', 'bg-purple-50', 'bg-orange-50'];
              return (
                <div key={i} className={`${bgColors[i]} rounded-xl p-4 border border-gray-100 text-center`}>
                  <div className={`w-10 h-10 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">{feature.title}</h3>
                  <p className="text-gray-600 text-xs">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-8 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-5">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              Traditional Tuition vs Eduzy
            </h2>
            <p className="text-gray-500 text-xs">Why smart parents are making the switch</p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-emerald-50 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-3 bg-emerald-100/50 p-3 border-b border-gray-200 font-semibold text-xs">
              <div className="text-gray-700">Feature</div>
              <div className="text-center text-gray-500">Traditional</div>
              <div className="text-center text-emerald-700">Eduzy</div>
            </div>
            {comparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 p-3 text-xs ${i !== comparison.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <div className="text-gray-700 font-medium">{row.feature}</div>
                <div className="text-center text-gray-400">{row.traditional}</div>
                <div className="text-center text-emerald-600 font-medium flex items-center justify-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {row.eduzy}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-8 px-4 bg-emerald-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">How Eduzy Works</h2>
            <p className="text-gray-500 text-sm">Simple setup, powerful results</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Parent Signs Up', desc: 'Register and add children with grade, board (CBSE or IB) and subjects', icon: Users, bg: 'bg-teal-50' },
              { step: '2', title: 'Child Learns', desc: 'AI-powered lessons aligned to their CBSE or IB board, adapting to their pace', icon: Brain, bg: 'bg-emerald-100' },
              { step: '3', title: 'Track Progress', desc: 'See quiz scores and areas needing attention', icon: BarChart3, bg: 'bg-cyan-50' }
            ].map((item, i) => (
              <div key={i} className={`text-center ${item.bg} rounded-xl p-5 shadow-sm border border-gray-100`}>
                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold text-white">
                  {item.step}
                </div>
                <item.icon className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                <h3 className="font-bold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-gray-600 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 px-4 bg-gradient-to-r from-emerald-500 to-cyan-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
            Don&apos;t Wait Until It&apos;s Too Late
          </h2>
          <p className="text-emerald-100 mb-5 text-sm max-w-xl mx-auto">
            Learning gaps in Classes 5-8 often become permanent. Give your child the complete support they need.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-white text-emerald-600 hover:bg-gray-100 px-8"
            >
              Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/eduwise')}
              className="border-white/50 text-white hover:bg-white/10 px-8"
            >
              View Plans
            </Button>
          </div>
          <p className="text-emerald-200 text-xs mt-3">
            No credit card required • {trialDays}-day free trial on EduWise
          </p>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
};

export default WhyEduzy;
